/** 飞书 URL 架构下的全部二级页（独立意图 + 完整正文结构） */
import { SUB_PAGE_EXTRAS } from './subPageExtras.js'

function helpArticle(config) {
	const {
		path,
		title,
		description,
		keywords,
		h1,
		parent,
		symptom,
		causes,
		checks,
		fixes,
		continuing,
	} = config
	return {
		path,
		title,
		description,
		keywords,
		h1,
		parent,
		sections: [
			{ h2: 'What is happening?', paragraphs: [symptom] },
			{ h2: 'Why this might happen', list: causes },
			{ h2: 'What to check first', list: checks },
			{ h2: 'How to fix it', steps: fixes },
			{ h2: 'If the problem continues', paragraphs: [continuing] },
		],
	}
}

function guideArticle(config) {
	const { path, title, description, keywords, h1, parent, intro, sections } = config
	return {
		path,
		title,
		description,
		keywords,
		h1,
		parent,
		sections: [{ paragraphs: [intro] }, ...sections],
	}
}

export const CORE_SUB_PAGES = {
	'features/privacy-features': guideArticle({
		path: '/features/privacy-features/',
		title: 'GB WhatsApp Pro Privacy Features Explained',
		description: 'Learn how GB WhatsApp Pro privacy toggles work, what they change for contacts, and what they cannot hide.',
		keywords: 'gb whatsapp pro privacy features, hide online status',
		h1: 'Privacy Features',
		parent: { label: 'Features', href: '/features/' },
		intro:
			'Privacy menus are the main reason many people try GB WhatsApp Pro. This page walks through common toggles—where they live in settings, what contacts actually see, and what still reaches WhatsApp servers. Nothing here replaces the Safety section on this site if you handle sensitive work or health conversations.',
		sections: [
			{
				h2: 'Where privacy settings usually live',
				paragraphs: [
					'Open GB Settings → Privacy on most builds. Some APKs nest options under Universal or hide labels like “Ghost mode” instead of “Hide online.” If a screenshot on the web does not match your phone, use Settings search for “online,” “ticks,” or “typing.”',
					'Change one toggle at a time and test with a trusted contact. Stacking five privacy options at once makes it hard to know which setting caused a bug or a missing notification.',
				],
				list: [
					'GB Settings → Privacy (primary menu on many v18.x builds)',
					'Settings → Account → Privacy (fallback path on some skins)',
					'Per-contact overrides when your build supports contact-specific privacy',
				],
			},
			{
				h2: 'Online status and last seen',
				paragraphs: [
					'Ghost-style modes and “hide online” options change what appears in the chat list and header when you are inside the app. They do not make you invisible to WhatsApp infrastructure, and they may behave differently in groups or communities.',
					'Last seen can often be set to Everyone, Contacts, or Nobody independently from online hiding. Contacts who cannot see last seen may still see you online if another toggle is off or if the app is open in the foreground.',
				],
				list: [
					'Hide online while the app is open (wording varies: Ghost, Freeze, etc.)',
					'Delay last seen updates compared with official defaults on some builds',
					'Show online only to selected contacts when per-contact privacy exists',
				],
			},
			{
				h2: 'Read receipts, ticks, and typing',
				paragraphs: [
					'Blue tick and double-tick controls delay or hide read status for senders. Senders may still infer that you read a message if you reply quickly or react with an emoji.',
					'Typing indicators can be hidden in one-to-one chats on some mods. Group typing dots may still appear because multiple participants trigger different server events.',
				],
				steps: [
					'Open GB Settings → Privacy and note current blue tick / second tick settings',
					'Toggle read receipts off for a test chat only if your build allows per-scope control',
					'Send a test message from another phone and confirm tick behavior before using at work',
					'Screenshot settings before updating APK—privacy toggles sometimes reset',
				],
			},
			{
				h2: 'Anti-delete, forwarding, and status privacy',
				paragraphs: [
					'Some builds advertise viewing deleted messages or limiting forwarding. Deleted-message viewing depends on the message still being cached locally; it is not a guarantee you can recover every removed message.',
					'Status privacy (who sees your status updates) may mirror official controls or add extra lists. Status viewers you block in the official app may not carry over if you switch clients without backup.',
				],
			},
			{
				h2: 'Groups vs one-to-one chats',
				paragraphs: [
					'Privacy toggles tuned for direct chats may not apply the same way in large groups. Admins and active members can infer activity from replies, mentions, or poll votes even when online status is hidden.',
					'If you rely on privacy for a sensitive thread, consider a small group or official WhatsApp with built-in Chat Lock rather than assuming mod toggles hide all signals.',
				],
			},
			{
				h2: 'Limits you should plan for',
				paragraphs: [
					'Mods cannot remove server-side logging or guarantee that WhatsApp will never restrict an account using unofficial clients. Privacy features change presentation to contacts—they are not a compliance or legal shield.',
					'After each APK update, reopen privacy menus and confirm toggles match your screenshots. If something reverted, reapply before returning to work groups.',
				],
			},
		],
	}),
	'features/customization': guideArticle({
		path: '/features/customization/',
		title: 'GB WhatsApp Pro Customization – Themes, Fonts & Layout',
		description: 'Customize GB WhatsApp Pro with themes, fonts, and chat backgrounds. Learn limits and update behavior.',
		keywords: 'gb whatsapp pro themes, gb whatsapp pro customization',
		h1: 'Customization Features',
		parent: { label: 'Features', href: '/features/' },
		intro:
			'Customization changes colors, fonts, and chat backgrounds without changing how messages send. This guide covers where theme menus hide in different APKs, how to avoid losing a layout after an update, and when heavy themes slow older phones.',
		sections: [
			{
				h2: 'Finding theme and font menus',
				paragraphs: [
					'Most builds place packs under GB Settings → Theme or Universal → Theme. Imported .xml theme files appear only on builds that explicitly support imports—dropping a file into Downloads is not enough if the menu has no import button.',
					'If nothing looks familiar after an update, search settings for “theme,” “style,” or “font” before assuming the feature was removed.',
				],
				list: [
					'Built-in gallery themes (one-tap apply)',
					'Downloaded theme packs from community lists (verify source)',
					'Font size and style sliders separate from bubble colors on some skins',
				],
			},
			{
				h2: 'Applying a theme safely',
				steps: [
					'Note your current theme name or screenshot the GB Settings → Theme screen',
					'Apply one new theme and wait for the UI to redraw—avoid switching rapidly',
					'Open a text-heavy group and a media thread to check contrast and readability',
					'Restart the app once if bubbles or icons look clipped on your Android version',
					'Keep a plain default theme in mind if you need to troubleshoot crashes later',
				],
			},
			{
				h2: 'Chat wallpaper and dark mode',
				paragraphs: [
					'Global wallpaper sets a background behind all chats; per-chat wallpaper overrides only that thread. Wallpapers do not change notification icons or the system share sheet.',
					'Dark mode may follow Android system theme, stay always-on inside the mod, or use a separate toggle. OLED phones save battery with true black backgrounds when the theme supports it.',
				],
				list: [
					'Chat menu → Wallpaper for a single conversation',
					'GB Settings → Theme → Dark/light switch when available',
					'Reduce motion or simplify themes if scrolling stutters on low-RAM phones',
				],
			},
			{
				h2: 'After APK updates',
				paragraphs: [
					'Updates can reset theme choice, font scale, and wallpaper paths. Reapply from your screenshot list instead of reinstalling immediately.',
					'Theme files tied to an old package name may not load on a new publisher line—export chat backups before jumping to a different mod brand just for cosmetics.',
				],
			},
			{
				h2: 'When customization causes problems',
				paragraphs: [
					'If the app crashes on open after a flashy theme, switch to the default theme from settings on a second launch attempt, or clear only theme cache if your build documents that path—avoid wiping all app data until chats are backed up.',
					'Unreadable contrast (light-on-light bubbles) is a sign to revert the theme; accessibility matters if you read messages outdoors.',
				],
			},
		],
	}),
	'features/media-sharing': guideArticle({
		path: '/features/media-sharing/',
		title: 'GB WhatsApp Pro Media Sharing Limits',
		description: 'Understand larger file sharing and status download options in GB WhatsApp Pro builds.',
		keywords: 'gb whatsapp pro media sharing, gb whatsapp pro file size',
		h1: 'Media Sharing',
		parent: { label: 'Features', href: '/features/' },
		intro:
			'Media features—larger sends, status tools, and extra compression options—still depend on your network, free storage, and what the recipient’s app accepts. This page explains practical limits and how to avoid failed uploads that look like account bans.',
		sections: [
			{
				h2: 'Send size and format limits',
				paragraphs: [
					'Some GB builds advertise higher megabyte caps for video or documents. The receiver’s client and WhatsApp servers may still compress or reject files that exceed current policy for their account type.',
					'Long uploads on mobile data can stall at 99% when the connection drops—use Wi‑Fi for clips over a few dozen megabytes and keep the screen awake until the progress bar finishes.',
				],
				list: [
					'Compress 4K video locally before sending when uploads repeat-fail',
					'Split large archives into parts or use a cloud link for work files',
					'Confirm free space on both phones when the recipient reports “download failed”',
				],
			},
			{
				h2: 'Photos, video quality, and documents',
				paragraphs: [
					'Quality sliders (HD photo, original quality video) appear on some mods. Higher quality increases upload time and data use; recipients on slow networks may only see a blurred preview until download completes.',
					'PDF and office documents behave like standard attachments—if the icon sends but the file will not open, ask the recipient to update their app or re-send on Wi‑Fi.',
				],
			},
			{
				h2: 'Auto-download and storage',
				paragraphs: [
					'Auto-download settings still fill internal storage when set to “all media on mobile data.” Pair large-send features with Wi‑Fi-only auto-download unless you routinely clear WhatsApp media folders.',
					'When the phone warns storage is full, incoming media fails before outgoing sends fail—free space before blaming the mod APK.',
				],
				steps: [
					'Settings → Storage and data → review auto-download for photos, audio, video, documents',
					'Delete old APK installers and duplicate downloads in Downloads',
					'Retry one failed video on Wi‑Fi after freeing at least 1 GB',
				],
			},
			{
				h2: 'Status viewing and save tools',
				paragraphs: [
					'Status save options usually require viewing a status once so the file caches locally. Saved clips may land in a public gallery folder—move sensitive items to a private album on shared phones.',
					'Saving someone else’s status for personal viewing is different from reposting it. Only keep media you have permission to store.',
				],
			},
			{
				h2: 'When media still will not send or download',
				paragraphs: [
					'Toggle airplane mode off, disable VPN, and confirm GB WhatsApp Pro has storage permission. If only one chat fails, the issue may be file-specific; if all media fails, check Help articles on photos and videos not downloading before reinstalling.',
				],
			},
		],
	}),
	'features/chat-features': guideArticle({
		path: '/features/chat-features/',
		title: 'GB WhatsApp Pro Chat Tools – Scheduler, Auto-Reply & Lock',
		description: 'Chat tools in GB WhatsApp Pro: scheduling, auto-reply, chat lock, and practical limits.',
		keywords: 'gb whatsapp pro auto reply, gb whatsapp pro message scheduler',
		h1: 'Chat Features',
		parent: { label: 'Features', href: '/features/' },
		intro:
			'Chat tools schedule sends, reply when you are away, or lock threads behind a PIN. They help on busy days and shared phones—but contacts should know when a message is automated, and locks do not replace full-device encryption.',
		sections: [
			{
				h2: 'Where chat tools hide in settings',
				paragraphs: [
					'Look under GB Settings for Scheduler, Auto Reply, Chat Lock, or Message tools. Labels move between releases; search “schedule,” “reply,” or “lock” if menus changed after an update.',
					'Some features work only in one-to-one chats; group schedulers or group auto-replies are less common and may be missing entirely on your build.',
				],
			},
			{
				h2: 'Message scheduler',
				paragraphs: [
					'Schedulers queue text or media for a later time. They need the app allowed to run in the background—battery savers on Xiaomi, Oppo, and Samsung can delay or kill scheduled sends until you open the app manually.',
				],
				steps: [
					'Compose the message and pick a time at least a few minutes ahead',
					'Confirm pending items in GB Settings if your build lists a scheduled queue',
					'Stay on Wi‑Fi for scheduled media; large files may fail if data saver kicks in',
					'Double-check time zone after travel—scheduled sends use phone local time',
				],
			},
			{
				h2: 'Auto-reply',
				paragraphs: [
					'Keep templates short and label them as automated (“Away until 5pm—auto reply”). Avoid auto-replies in family groups unless everyone expects a bot-style response.',
					'Auto-reply does not replace reading urgent messages later; it only sends the first template when rules match.',
				],
				list: [
					'Work-hours auto-reply vs always-on vacation message',
					'Exclude specific contacts when your build supports allow lists',
					'Turn off auto-reply before OTP or banking SMS threads forwarded to WhatsApp',
				],
			},
			{
				h2: 'Chat lock and app lock',
				paragraphs: [
					'Chat lock protects individual threads with PIN or fingerprint. App lock gates the whole client. Neither hides notifications on the lock screen unless you also adjust Android notification privacy settings.',
					'Forgotten PINs may require clearing lock data and re-verifying the app—back up before experimenting on your main number.',
				],
			},
			{
				h2: 'Filters, pins, and broadcast lists',
				paragraphs: [
					'Pinned chats and custom filters help busy inboxes. Broadcast lists behave like official WhatsApp: recipients see replies only to you, not to each other.',
					'If a scheduled message targets a broadcast list, verify the list still exists after restore from backup—lists do not always migrate across mod versions.',
				],
			},
			{
				h2: 'When tools stop working after an update',
				paragraphs: [
					'Reopen GB Settings and re-enable scheduler or auto-reply toggles; updates sometimes default them off. If sends stay pending, disable battery optimization for GB WhatsApp Pro once, then retry on Wi‑Fi.',
				],
			},
		],
	}),
	'installation/android': guideArticle({
		path: '/installation/android/',
		title: 'Install GB WhatsApp Pro on Android – Detailed Steps',
		description: 'Android-specific GB WhatsApp Pro installation: permissions, verification, and first-run setup.',
		keywords: 'gb whatsapp pro android install',
		h1: 'Installation on Android',
		parent: { label: 'Installation', href: '/installation/' },
		intro: 'These steps assume a standard Android phone with APK sideloading allowed.',
		sections: [
			{
				h2: 'Prepare the device',
				list: [
					'Android 5.0 or newer (confirm in Settings → About phone)',
					'Stable Wi‑Fi for download and backup',
					'At least 500 MB free space beyond the APK size',
				],
			},
			{
				h2: 'Install and verify',
				steps: [
					'Download from <a href="/download/">the download page</a>.',
					'Open the APK and approve the installing app if prompted.',
					'Launch GB WhatsApp Pro and enter your phone number.',
					'Enter the SMS verification code when it arrives.',
					'Restore backup if you exported chats earlier.',
				],
			},
		],
	}),
	'installation/without-uninstalling': guideArticle({
		path: '/installation/without-uninstalling/',
		title: 'Install GB WhatsApp Pro Without Uninstalling WhatsApp',
		description: 'When dual installation may work, and when you must uninstall official WhatsApp first.',
		keywords: 'gb whatsapp pro dual app',
		h1: 'Install Without Uninstalling',
		parent: { label: 'Installation', href: '/installation/' },
		intro: 'Some APKs use a different package name so they can sit beside official WhatsApp. This is not universal.',
		sections: [
			{
				h2: 'When dual install is possible',
				paragraphs: [
					'If the mod publisher documents a separate package, Android may show two icons. You still manage two accounts and two backup paths—do not mix them.',
				],
			},
			{
				h2: 'When you must replace the official app',
				paragraphs: [
					'If Android reports a signature conflict, you cannot install over official WhatsApp. Back up, then decide whether to migrate or stay on the official client.',
				],
			},
		],
	}),
	'installation/backup-chats': guideArticle({
		path: '/installation/backup-chats/',
		title: 'Back Up GB WhatsApp Pro Chats',
		description: 'Back up chats before installing, updating, or reinstalling GB WhatsApp Pro.',
		keywords: 'gb whatsapp pro backup chats',
		h1: 'Back Up Chats',
		parent: { label: 'Installation', href: '/installation/' },
		intro: 'Backups are your safety net whenever you sideload APKs.',
		sections: [
			{
				h2: 'Local and cloud backups',
				paragraphs: [
					'Use in-app backup to Google Drive or local storage if your build supports it. Note the timestamp and file size after completion.',
				],
			},
			{
				h2: 'Before destructive steps',
				paragraphs: [
					'Never uninstall because of an error until you confirm the backup opens on the same or another device.',
				],
			},
		],
	}),
	'installation/restore-chats': guideArticle({
		path: '/installation/restore-chats/',
		title: 'Restore GB WhatsApp Pro Chat Backups',
		description: 'Restore chats after installing GB WhatsApp Pro or switching APK versions.',
		keywords: 'gb whatsapp pro restore chats',
		h1: 'Restore Chats',
		parent: { label: 'Installation', href: '/installation/' },
		intro: 'Restoration usually happens on first launch after verification.',
		sections: [
			{
				h2: 'During setup',
				steps: [
					'Verify your phone number.',
					'When prompted, choose Restore from backup.',
					'Pick the latest backup file you trust.',
					'Wait for media to download on Wi‑Fi if needed.',
				],
			},
			{
				h2: 'If restore fails',
				paragraphs: [
					'Confirm the backup came from a compatible version. See <a href="/update/update-failed/">update failed help</a> before deleting the app.',
				],
			},
		],
	}),
	'update/without-losing-chats': guideArticle({
		path: '/update/without-losing-chats/',
		title: 'Update GB WhatsApp Pro Without Losing Chats',
		description: 'Manual update flow that protects chat history when upgrading GB WhatsApp Pro.',
		keywords: 'gb whatsapp pro update without losing chats',
		h1: 'Update Without Losing Chats',
		parent: { label: 'Update', href: '/update/' },
		intro: 'Most chat loss happens when users uninstall too quickly. Follow order: backup → install over → verify.',
		sections: [
			{
				h2: 'Safe update order',
				steps: [
					'Create a fresh backup via <a href="/installation/backup-chats/">backup steps</a>.',
					'Download the new APK from <a href="/download/">download</a>.',
					'Install over the existing app when Android offers Update.',
					'Open chats and spot-check recent threads and media.',
				],
			},
		],
	}),
	'update/check-version': guideArticle({
		path: '/update/check-version/',
		title: 'How to Check Your GB WhatsApp Pro Version',
		description: 'Find your installed GB WhatsApp Pro version and compare it with the latest verified APK.',
		keywords: 'gb whatsapp pro version check',
		h1: 'Check Your Version',
		parent: { label: 'Update', href: '/update/' },
		intro: 'Knowing your build number prevents downloading the wrong APK.',
		sections: [
			{
				h2: 'Inside the app',
				paragraphs: [
					'Open Settings → App info or Help (labels vary by build). Compare the version string with v18.80 on our download page.',
				],
			},
			{
				h2: 'On Android system settings',
				paragraphs: [
					'Settings → Apps → GB WhatsApp Pro → App details shows the package version installed by the system.',
				],
			},
		],
	}),
	'update/update-failed': guideArticle({
		path: '/update/update-failed/',
		title: 'GB WhatsApp Pro Update Failed',
		description: 'Recover when a GB WhatsApp Pro APK update will not install over your current version.',
		keywords: 'gb whatsapp pro update failed',
		h1: 'Update Failed',
		parent: { label: 'Update', href: '/update/' },
		intro: 'Failed updates are common when package signatures or version codes conflict.',
		sections: [
			{
				h2: 'First response',
				list: [
					'Confirm backup completed successfully',
					'Re-download the APK on Wi‑Fi',
					'Check Android version compatibility',
					'Compare file size with the download page',
				],
			},
			{
				h2: 'Before uninstalling',
				paragraphs: [
					'Uninstall only if support docs require it and your backup is verified. See <a href="/installation/restore-chats/">restore chats</a> afterward.',
				],
			},
		],
	}),
	'safety/account-ban-risk': guideArticle({
		path: '/safety/account-ban-risk/',
		title: 'GB WhatsApp Pro Account Ban Risk',
		description: 'Why unofficial WhatsApp clients may face restrictions and how to reduce avoidable triggers.',
		keywords: 'gb whatsapp pro ban risk',
		h1: 'Account Ban Risk',
		parent: { label: 'Safety', href: '/safety/' },
		intro: 'WhatsApp may limit accounts connected through modified clients. No mod removes that policy risk.',
		sections: [
			{
				h2: 'Reported signals',
				list: [
					'Temporary inability to send messages',
					'Repeated verification requests',
					'Official WhatsApp warning screens',
				],
			},
			{
				h2: 'Practical habits',
				paragraphs: [
					'Avoid bulk spam, keep backups, and be ready to migrate to official WhatsApp if your account is business-critical.',
				],
			},
		],
	}),
	'safety/privacy-risks': guideArticle({
		path: '/safety/privacy-risks/',
		title: 'GB WhatsApp Pro Privacy Risks',
		description: 'Privacy limits of modified WhatsApp clients and what extra toggles cannot protect.',
		keywords: 'gb whatsapp pro privacy risks',
		h1: 'Privacy Risks',
		parent: { label: 'Safety', href: '/safety/' },
		intro: 'Privacy switches change UI behavior; they are not end-to-end guarantees against all observers.',
		sections: [
			{
				h2: 'Third-party distribution',
				paragraphs: [
					'APK publishers control update channels and embedded analytics. Review permissions on each install.',
				],
			},
		],
	}),
	'safety/malware-and-fake-apks': guideArticle({
		path: '/safety/malware-and-fake-apks/',
		title: 'Fake GB WhatsApp Pro APK Files',
		description: 'Identify tampered GB WhatsApp Pro downloads before installation.',
		keywords: 'fake gb whatsapp pro apk',
		h1: 'Malware and Fake APKs',
		parent: { label: 'Safety', href: '/safety/' },
		intro: 'Repackaged APKs can steal credentials or serve ads. Compare source, size, and permissions.',
		sections: [
			{
				h2: 'Red flags',
				list: [
					'Download pages with no version or size information',
					'APK much smaller than the verified build',
					'Requests for unrelated permissions (SMS, admin, etc.)',
				],
			},
			{
				h2: 'Safer habit',
				paragraphs: [
					'Use the link on <a href="/download/">download</a> and delete unknown copies from Downloads.',
				],
			},
		],
	}),
	'help/not-installing': helpArticle({
		path: '/help/not-installing/',
		title: 'GB WhatsApp Pro Not Installing',
		description: 'Fix GB WhatsApp Pro install failures on Android.',
		keywords: 'gb whatsapp pro not installing',
		h1: 'Not Installing',
		parent: { label: 'Help Center', href: '/help/' },
		symptom:
			'Android shows “App not installed”, “Parse error”, or the install button disappears without finishing.',
		causes: [
			'Insufficient storage',
			'Corrupted or incomplete APK download',
			'Android version below minimum',
			'Play Protect or OEM security block',
			'Package conflict with existing WhatsApp install',
		],
		checks: [
			'Free at least 1 GB storage',
			'Re-download on Wi‑Fi from <a href="/download/">download</a>',
			'Confirm Android 5.0+ in system settings',
			'Read the full Play Protect message before allowing install',
		],
		fixes: [
			'Clear Downloads and fetch the APK again.',
			'Enable install permission for the browser or Files app you used.',
			'If signature conflict appears, read <a href="/installation/without-uninstalling/">dual install guide</a>.',
			'Reboot and retry once before trying another APK source.',
		],
		continuing:
			'Follow <a href="/installation/android/">Android installation</a> from the top. If every APK fails, test with official WhatsApp to rule out device storage faults.',
	}),
	'help/not-opening': helpArticle({
		path: '/help/not-opening/',
		title: 'GB WhatsApp Pro Not Opening',
		description: 'Fix GB WhatsApp Pro stuck on splash or closing immediately.',
		keywords: 'gb whatsapp pro not opening',
		h1: 'Not Opening',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'The app closes right after the logo or freezes on the first screen.',
		causes: ['Corrupted install', 'Incompatible update', 'Low memory', 'Broken cache'],
		checks: ['Recent APK update?', 'Free RAM and storage', 'Other apps crashing too?'],
		fixes: [
			'Force stop → clear cache (not data unless backed up).',
			'Reboot the phone.',
			'Reinstall the same verified APK from download.',
		],
		continuing: 'See <a href="/help/keeps-crashing/">keeps crashing</a> if the app opens then dies during chats.',
	}),
	'help/keeps-crashing': helpArticle({
		path: '/help/keeps-crashing/',
		title: 'GB WhatsApp Pro Keeps Crashing',
		description: 'Stop GB WhatsApp Pro crash loops after updates.',
		keywords: 'gb whatsapp pro crashing',
		h1: 'Keeps Crashing',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'GB WhatsApp Pro closes during chats, settings, or media viewing.',
		causes: ['Buggy build for your Android version', 'Low storage', 'Corrupted media cache'],
		checks: ['Version matches download page?', 'Storage above 15% free?', 'Crash only after one chat?'],
		fixes: [
			'Update or roll back using <a href="/update/">update guide</a>.',
			'Clear cache, restart phone.',
			'Test on Wi‑Fi without VPN.',
		],
		continuing: 'Capture Android version + app version before trying a different APK line.',
	}),
	'help/messages-not-sending': helpArticle({
		path: '/help/messages-not-sending/',
		title: 'GB WhatsApp Pro Messages Not Sending',
		description: 'Fix stuck clocks and failed sends in GB WhatsApp Pro.',
		keywords: 'gb whatsapp pro messages not sending',
		h1: 'Messages Not Sending',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'Messages stay on one grey tick or show not sent errors.',
		causes: ['No internet', 'Background restriction', 'Account limit', 'Server outage'],
		checks: ['Browser loads websites?', 'Mobile data allowed for app?', 'Official WhatsApp works on same network?'],
		fixes: [
			'Toggle airplane mode off/on.',
			'Disable battery saver for GB WhatsApp Pro.',
			'Force stop and reopen the app.',
			'Check time zone and date auto settings.',
		],
		continuing: 'If only one contact fails, ask them to refresh their client. Persistent bans may need official WhatsApp—see <a href="/safety/account-ban-risk/">ban risk</a>.',
	}),
	'help/messages-not-receiving': helpArticle({
		path: '/help/messages-not-receiving/',
		title: 'GB WhatsApp Pro Messages Not Receiving',
		description: 'Fix delayed or missing incoming messages.',
		keywords: 'gb whatsapp pro not receiving messages',
		h1: 'Messages Not Receiving',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'Friends report delivered messages but your phone stays silent until you open the app.',
		causes: ['Background sync blocked', 'Notification issues', 'DND mode inside mod', 'Data saver'],
		checks: ['Notifications enabled?', 'DND inside app off?', 'Compare with official WhatsApp on same phone'],
		fixes: [
			'Allow unrestricted background data.',
			'Turn off mod DND features temporarily.',
			'Disable adaptive battery for the app on strict OEM skins.',
		],
		continuing: 'See <a href="/help/notifications-not-working/">notifications not working</a> if alerts are the main symptom.',
	}),
	'help/photos-videos-not-downloading': helpArticle({
		path: '/help/photos-videos-not-downloading/',
		title: 'GB WhatsApp Pro Media Not Downloading',
		description: 'Fix photos and videos that will not download in GB WhatsApp Pro.',
		keywords: 'gb whatsapp pro media not downloading',
		h1: 'Photos & Videos Not Downloading',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'Media spins forever or shows download failed.',
		causes: ['Full storage', 'SD card path issues', 'Weak network', 'Corrupted cache'],
		checks: ['Storage free space', 'Download on Wi‑Fi', 'Can you send media outbound?'],
		fixes: [
			'Clear GB WhatsApp Pro cache.',
			'Move storage to internal if SD card is read-only.',
			'Delete old large files from WhatsApp media folders carefully.',
		],
		continuing: 'See <a href="/help/storage-problems/">storage problems</a> for cleanup order.',
	}),
	'help/notifications-not-working': helpArticle({
		path: '/help/notifications-not-working/',
		title: 'GB WhatsApp Pro Notifications Not Working',
		description: 'Restore message notifications on Android.',
		keywords: 'gb whatsapp pro notifications',
		h1: 'Notifications Not Working',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'No banner or sound when messages arrive unless the app is open.',
		causes: ['Channel disabled', 'OEM battery kill', 'Mod DND', 'Focus mode'],
		checks: ['Android notification channel on?', 'App not muted in system settings?'],
		fixes: [
			'Re-enable notification categories in Android app settings.',
			'Whitelist app from battery optimization.',
			'Disable mod DND temporarily to test.',
		],
		continuing: 'If messages arrive late, also read <a href="/help/messages-not-receiving/">messages not receiving</a>.',
	}),
	'help/verification-code-not-received': helpArticle({
		path: '/help/verification-code-not-received/',
		title: 'GB WhatsApp Pro Verification Code Not Received',
		description: 'SMS verification troubleshooting for GB WhatsApp Pro setup.',
		keywords: 'gb whatsapp pro verification code',
		h1: 'Verification Code Not Received',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'SMS code never arrives during number verification.',
		causes: ['Carrier delay', 'Wrong number', 'Roaming', 'Too many attempts'],
		checks: ['SIM active for SMS?', 'Airplane mode off?', 'Correct country code?'],
		fixes: [
			'Wait full countdown before requesting again.',
			'Insert SIM in another phone briefly to test SMS reception.',
			'Try call-me verification if offered.',
		],
		continuing: 'Never share codes with third-party “support” chats. WhatsApp and GBWhatPro.org will never ask for your SMS code.',
	}),
	'help/storage-problems': helpArticle({
		path: '/help/storage-problems/',
		title: 'GB WhatsApp Pro Storage Problems',
		description: 'Free space and cache issues affecting GB WhatsApp Pro.',
		keywords: 'gb whatsapp pro storage',
		h1: 'Storage Problems',
		parent: { label: 'Help Center', href: '/help/' },
		symptom: 'Install or media fails citing insufficient storage even when some space appears free.',
		causes: ['Cache bloat', 'Duplicate media', 'System reserved space'],
		checks: ['Settings → Storage breakdown', 'Large WhatsApp media folders'],
		fixes: [
			'Clear cache inside app settings.',
			'Move backups to PC or cloud and delete local copies.',
			'Uninstall unused apps before APK upgrades.',
		],
		continuing: 'Back up chats before deleting WhatsApp media folders manually.',
	}),
}

for (const page of Object.values(CORE_SUB_PAGES)) {
	const extra = SUB_PAGE_EXTRAS[page.path]
	if (extra && extra.length) {
		page.sections = [...page.sections, ...extra]
	}
}
